import { z } from 'zod';
import { UserClassificationSchema, UserStatusSchema, UserTypeSchema, refineExternalMember } from '../../entities/user/user.schema';
import { TenantDescriptionSchema, TenantSlugSchema, TenantTypeSchema } from '../../entities/tenant/tenant.schema';
import { EntityOrSuggestionRefSchema } from '../../entities/entity-suggestion';
import { ProcessCategorySchema } from '../../entities/process/process.schema';
import { ChainSwimlaneSchema, ProcessChainSchema } from '../../entities/process-chain/process-chain.schema';
import { ChartPointLabelKeySchema } from '../../shared/chart-spec';
import { MODEL_IMAGE_MAX_BYTES } from '../../shared/chat-attachment';
import { CapacityBindingSchema, ProcessCapacitySchema } from '../../shared/capacity';
import { ObjectIdSchema } from '../../shared/object-id.schema';
import { REGION_GET_MANY_MAX, RegionListSchema, RegionSearchSchema } from '../../entities/region/region.schema';
/**
 * Execute App Function Request Schema
 * POST /api/v2/apps/:appId/fn/:functionName
 * Params are arbitrary — passed through to the function handler
 */ export const ExecuteAppFunctionRequestSchema = z.record(z.string(), z.unknown());
/**
 * Execute App Function Response Schema
 */ export const ExecuteAppFunctionResponseSchema = z.object({
    result: z.unknown()
});
/**
 * Execute App Function Async Response Schema
 * POST /api/v2/apps/:appId/fn/:functionName/async
 * The run itself is polled via GET /api/v2/apps/:appId/storage/_runs/:runId
 */ export const ExecuteAppFunctionAsyncResponseSchema = z.object({
    runId: z.string()
});
/**
 * App Function Run Schema
 * Lifecycle document written by the platform into the reserved `_runs` collection
 */ export const AppFunctionRunStatusSchema = z.enum([
    'running',
    'completed',
    'failed'
]);
export const AppFunctionRunSchema = z.object({
    _id: z.string(),
    functionName: z.string(),
    /** User who initiated the run — reads are scoped to this user. */ userId: z.string(),
    status: AppFunctionRunStatusSchema,
    result: z.unknown().optional(),
    error: z.string().optional(),
    startedAt: z.string(),
    /** Declared function timeout — lets readers detect stale `running` docs. */ timeoutMs: z.number().int().positive(),
    finishedAt: z.string().optional()
});
/**
 * App Org Member Schema
 * The tenant member directory entry the app data proxy hands to app functions
 * under the `read:org-members` manifest permission.
 *
 * This is a STRICT allowlist, not a projection of the user document: billing
 * rates, capacity figures and survey answers are the sensitivity category that
 * must never reach an app sandbox or an AI prompt through the directory. The
 * schema is the single definition shared by the producing data-proxy host and
 * the consuming app SDK, so widening it is a deliberate, reviewable edit.
 */ export const AppOrgMemberSchema = z.object({
    userId: z.string(),
    /** Rendered "first last" — apps never reassemble name parts themselves. */ name: z.string(),
    email: z.string(),
    /** Department name, or null when the member has none. */ department: z.string().nullable(),
    /** Re-join key for `data.departments.list` — abbreviation and manager live there. */ departmentId: z.string().nullable(),
    /** Names of the member's Stelle plus any additional OrgFunction links. */ orgFunctions: z.array(z.string()),
    /**
   * The member's Stelle as an entity. `orgFunctions` stays a name array — it is
   * a shipped field other apps read — so the Stelle appears twice on purpose:
   * once as a name among the links, once here with the id and the description a
   * role-aware answer needs.
   */ mainRole: z.object({
        _id: z.string(),
        name: z.string(),
        description: z.string().optional()
    }).nullable(),
    /** `external` marks a non-employee member so UIs can label them. */ classification: UserClassificationSchema,
    /**
   * Lifecycle status. The directory deliberately lists non-active members
   * (suspended, inactive, pending) so plans can keep referencing them — this
   * widens the `read:org-members` contract on purpose: app sandboxes and AI
   * prompts now see who is blocked or not yet activated. Deleted members stay
   * out.
   */ status: UserStatusSchema
});
/**
 * App Countable Process Step Schema
 * The raw step view the app data proxy hands out for `resolveRefs: false` —
 * the shape a pure interface count needs and nothing else.
 *
 * Like {@link AppOrgMemberSchema} this is a STRICT allowlist, and the parse is
 * the security boundary: the raw branch skips the display-schema parse, so
 * without it internal recording telemetry (`recordingSessions`,
 * `activeRecordingMs`, `lastHeartbeatAt` — off every read contract) would reach
 * the app sandbox. Zod's unknown-key stripping is the filter.
 *
 * Refs stay unresolved (`{ type, id }`) — resolving them is the very work this
 * view skips. Legacy `inputs[].sourceStepId` is absent for parity, not privacy:
 * the display schema strips it, so counting it here would make the raw path see
 * edges the resolving path cannot.
 */ export const AppCountableProcessStepSchema = z.object({
    _id: z.string(),
    assignedTo: EntityOrSuggestionRefSchema.nullable().optional(),
    /** Stored free-form on the recording; only `'System'` is branched on. */ assignedToModel: z.string().nullable().optional(),
    helpers: z.object({
        systems: z.array(EntityOrSuggestionRefSchema)
    }),
    inputs: z.array(z.object({
        inputType: z.string(),
        sourceSteps: z.array(z.object({
            stepId: z.string()
        })).optional()
    }))
});
/**
 * The `ctx.data` analyst read operations (ADR 0020).
 *
 * Request schemas are parsed at the DataProxyHost boundary before a repository
 * is touched: unlike the fifteen arms that shipped before them, these carry
 * caller-chosen ids, limits and enums straight from untrusted app code. Result
 * schemas are the shared definition of the producing host and the consuming
 * SDK, so an operation's payload cannot drift between the two.
 */ export const AppOrgMemberGetRequestSchema = z.object({
    id: ObjectIdSchema
});
/** Shared by every operation whose whole input is "which process". */ export const AppProcessScopedRequestSchema = z.object({
    processId: ObjectIdSchema
});
export const AppProcessRatingsRequestSchema = z.object({
    processIds: z.array(ObjectIdSchema).min(1).max(100)
});
/**
 * `avgRating: null` already means "nobody rated this process", which is why an
 * UNRATABLE process is absent from the result rather than nulled — the two are
 * different statements and only one of them is true.
 */ export const AppProcessRatingSchema = z.object({
    processId: z.string(),
    avgRating: z.number().nullable(),
    ratedCount: z.number().int()
});
/**
 * Owner types an app may rank by embedding — the four entities a lookup tool
 * blends a semantic ordering into. Deliberately NOT the whole
 * `EmbeddingOwnerType` union: `ProcessStepRecording` and `Survey` are served
 * WITH their text by `data.knowledge.search`, which projects every hit onto a
 * parent process.
 *
 * `'Process'` is here even though that operation exists, because the two answer
 * different questions: `data.knowledge.search` ranks a process by the best of
 * its own, its steps' and its surveys' embeddings, while `lookupProcesses`
 * blends a PROCESS-only ordering into a name search. Substituting one for the
 * other would change which processes a name query returns.
 */ const APP_EMBEDDING_RANK_OWNER_TYPES = [
    'System',
    'OrgFunction',
    'Process',
    'Department'
];
/**
 * `topK` is not a parameter: the ids only ever feed a lookup's rank fusion,
 * whose semantic side is pinned to one search page core-side exactly as it is
 * for the core tools.
 */ export const AppEmbeddingRankRequestSchema = z.object({
    ownerType: z.enum(APP_EMBEDDING_RANK_OWNER_TYPES),
    query: z.string().min(2)
});
/**
 * IDS ONLY — never a name, a score or a snippet, and only ids the app's scope
 * already reaches. The app re-reads whatever it wants to show through the
 * authorized lookup arms, so this operation discloses ordering and nothing
 * else. `degraded` means the index was unavailable and the caller must serve
 * its lexical ranking instead.
 */ export const AppEmbeddingRankResultSchema = z.object({
    ids: z.array(z.string()),
    degraded: z.boolean()
});
/**
 * Per-step survey summary of one process — the qualitative half of a process
 * detail view.
 *
 * Its own operation rather than an app-side reduction over
 * `data.surveys.listByProcess`, because that arm serves TERMINAL rows only and
 * carries no comment text: `pendingCount` (the "still owed" signal) and
 * `comments` are structurally underivable from it, and both are answers the
 * tool gives today.
 *
 * `comments` is free-text written by the people doing the work, so the whole
 * operation sits behind the same `ReadSensitive` gate as the surveys arm.
 */ export const AppStepSurveyInsightsSchema = z.object({
    stepId: z.string(),
    /** Mean stars over COMPLETED surveys; `null` when nobody rated the step. */ avgStars: z.number().nullable(),
    ratedCount: z.number().int(),
    completedCount: z.number().int(),
    pendingCount: z.number().int(),
    dismissedCount: z.number().int(),
    comments: z.array(z.string())
});
export const AppKnowledgeSearchRequestSchema = z.object({
    query: z.string().min(2),
    /** Processes to return, 1–25 (default 10). Core oversamples the index 3×. */ limit: z.number().int().min(1).max(25).optional()
});
/**
 * One authorized hit. Ranking, per-process winner selection and the degraded
 * note are APP-side: core decides only what may be seen, never what scores
 * best, so a process whose every hit was dropped is simply absent.
 */ export const AppKnowledgeHitSchema = z.object({
    process: z.object({
        _id: z.string(),
        name: z.string(),
        description: z.string().optional()
    }),
    matchedStep: z.object({
        _id: z.string(),
        name: z.string()
    }).optional(),
    matchedSurveyComment: z.string().optional(),
    score: z.number()
});
export const AppKnowledgeSearchResultSchema = z.object({
    hits: z.array(AppKnowledgeHitSchema),
    /** The index timed out; the app decides what to tell the model. */ degraded: z.boolean()
});
export const APP_ENTITY_COUNT_GROUP_BY = [
    'department',
    'category',
    'owner'
];
/** The process landscape classification, from the entity that defines it. */ export const APP_PROCESS_CATEGORIES = ProcessCategorySchema.options;
export const AppEntityCountRequestSchema = z.object({
    entityType: z.literal('process'),
    groupBy: z.enum(APP_ENTITY_COUNT_GROUP_BY),
    filter: z.object({
        category: z.enum(APP_PROCESS_CATEGORIES).optional()
    }).optional()
});
export const AppEntityCountGroupSchema = z.object({
    /** Null exactly when the label is a placeholder — see `labelKey`. */ key: z.string().nullable(),
    label: z.string(),
    count: z.number().int(),
    labelKey: ChartPointLabelKeySchema.optional()
});
export const AppEntityCountResultSchema = z.object({
    entityType: z.literal('process'),
    groupBy: z.enum(APP_ENTITY_COUNT_GROUP_BY),
    totalMatching: z.number().int(),
    groupCount: z.number().int(),
    /** `groupCount` exceeds the returned rows — never sum `groups` for a total. */ truncated: z.boolean(),
    groups: z.array(AppEntityCountGroupSchema)
});
export const AppEntityUsageRequestSchema = z.object({
    target: z.enum([
        'system',
        'person'
    ]),
    targetId: ObjectIdSchema
});
/**
 * `null` for a target that is absent OR unreadable — the two collapse on
 * purpose, so tracing a system never confirms that it exists.
 */ export const AppEntityUsageResultSchema = z.object({
    processes: z.array(z.object({
        _id: z.string(),
        name: z.string(),
        department: z.string().optional()
    })),
    /** Candidate processes scanned BEFORE authorization dropped any. */ scanned: z.number().int(),
    capReached: z.boolean()
}).nullable();
export const AppOrgGraphRequestSchema = z.object({
    mode: z.enum([
        'departmentMembers',
        'departmentProcesses',
        'orgFunctionMembers'
    ]),
    anchorId: ObjectIdSchema
});
export const AppOrgGraphRowSchema = z.object({
    _id: z.string(),
    name: z.string(),
    email: z.string().optional(),
    mainRole: z.string().optional(),
    isLead: z.boolean().optional()
});
export const AppOrgGraphResultSchema = z.object({
    rows: z.array(AppOrgGraphRowSchema),
    /** Authorized rows before the cap — never the pre-authorization scan size. */ scanned: z.number().int(),
    capReached: z.boolean()
});
/**
 * Candidate documents one lookup scan may touch. The app ranks the page it is
 * handed, so this ceiling — not the number of rows the model asked for — is
 * what `limit` names; `offset` continues the scan past it.
 */ export const APP_LOOKUP_CANDIDATE_LIMIT = 500;
/**
 * Rows a lookup SERVES when the model named no limit.
 *
 * One number, deliberately: it is both the semantic ranking's topK core-side
 * and the lexical page the app cuts, and a search whose two halves ranked
 * different numbers of candidates would fuse two different questions.
 */ export const APP_LOOKUP_SEARCH_LIMIT = 25;
/** The same for "show me everything" — no existence question, so a wider page. */ export const APP_LOOKUP_BROWSE_LIMIT = 50;
/**
 * The browse/search input of the lookup path. `query` is the top-level regex
 * pre-filter only (ADR 0020 search-parity invariants) — an empty or unusable
 * query browses rather than matching nothing.
 */ const LookupListRequestSchema = z.object({
    query: z.string().optional(),
    limit: z.number().int().min(1).optional(),
    offset: z.number().int().min(0).optional()
});
export const AppArtifactListRequestSchema = LookupListRequestSchema;
export const AppArtifactGetRequestSchema = z.object({
    id: ObjectIdSchema
});
/** A strict allowlist, as {@link AppOrgMemberSchema} is — the parse is the boundary. */ export const AppArtifactSchema = z.object({
    _id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    type: z.string().optional(),
    fileUrl: z.string().optional()
});
export const AppExternalContactListRequestSchema = LookupListRequestSchema;
export const AppExternalContactGetRequestSchema = z.object({
    id: ObjectIdSchema
});
export const AppExternalContactSchema = z.object({
    _id: z.string(),
    name: z.string(),
    email: z.string().optional(),
    description: z.string().optional(),
    type: z.string()
});
/** Entities one model-triggered capacity query may name. */ export const APP_CAPACITY_ENTITY_ID_LIMIT = 25;
export const APP_CAPACITY_ENTITY_TYPES = [
    'process',
    'system',
    'step'
];
export const AppProcessCapacityResultSchema = ProcessCapacitySchema.pick({
    overall: true,
    perStep: true,
    coverage: true
});
/**
 * Over-cap `entityIds` is a REQUEST ERROR, not a truncation: a silently
 * shortened set would be answered as if it were the whole question.
 */ export const AppEntityCapacityRequestSchema = z.object({
    entityType: z.enum(APP_CAPACITY_ENTITY_TYPES),
    entityIds: z.array(ObjectIdSchema).min(1).max(APP_CAPACITY_ENTITY_ID_LIMIT)
});
/** `unavailableIds` merges "no such id" and "not readable" — never reveal which. */ export const AppEntityCapacityResultSchema = z.object({
    entities: z.array(z.object({
        _id: z.string(),
        name: z.string(),
        /** `null` means no survey data was recorded, never "withheld". */ capacity: CapacityBindingSchema.nullable()
    })),
    unavailableIds: z.array(z.string())
});
export const AppTombstoneProbeRequestSchema = z.object({
    entityType: z.enum([
        'org-member',
        'system',
        'org-function',
        'external-contact'
    ]),
    query: z.string().optional(),
    id: ObjectIdSchema.optional()
});
/**
 * A probe discloses exactly existence, canonical name and deletion — never any
 * other field of a soft-deleted record.
 */ export const AppTombstoneProbeResultSchema = z.object({
    matches: z.array(z.object({
        _id: z.string(),
        name: z.string(),
        deleted: z.literal(true)
    })),
    truncated: z.boolean()
});
export const AppProcessChainMembersRequestSchema = z.object({
    chainId: ObjectIdSchema
});
/**
 * One process of a flattened chain. `chainPath` is the chain NAMES from the
 * root down to the chain that holds the mapping, so a member keeps the context
 * it was read under; `swimlane` and `order` are the mapping's own, not a
 * position in the flattened list.
 *
 * Spelled out rather than extended from `ProcessSwimlaneMappingSchema`: that one
 * is the create/edit REQUEST contract, and its `order >= 1` floor is a write
 * rule. The mongoose prop is a bare Number, so re-applying the floor to this
 * READ would fail a whole chain analysis over one imported row.
 */ export const AppProcessChainMemberSchema = z.object({
    processId: z.string(),
    swimlane: ChainSwimlaneSchema,
    order: z.number(),
    chainPath: z.array(z.string())
});
export const AppProcessChainMembersResponseSchema = z.object({
    chain: ProcessChainSchema.pick({
        _id: true,
        name: true,
        description: true,
        category: true,
        e2eLabel: true,
        typicalInputs: true
    }),
    members: z.array(AppProcessChainMemberSchema)
});
/**
 * `ctx.files.create` — the app-file upload arm of the worker file proxy.
 *
 * Bytes cross the worker boundary by structured clone, so they arrive as a
 * plain `Uint8Array` (a `Buffer` loses its brand); the host reconstructs the
 * `Buffer`. There is no `size` field on purpose: the stored size is measured
 * from the received bytes, never taken from the caller. MIME allowlist and
 * size cap are enforced against the manifest's `files` declaration.
 */ export const AppFileCreateRequestSchema = z.object({
    name: z.string().min(1),
    mimeType: z.string().min(1),
    data: z.instanceof(Uint8Array),
    /**
   * Opts this file out of the owner scoping a worker-written file carries by
   * default (see `visibility` on the AppFile schema). Set it for a
   * DERIVATIVE of shared material — the converter's office→PDF rendition of a
   * document every entitled user may open — never for the private result of one
   * person's request.
   */ shared: z.boolean().optional()
});
/**
 * `ctx.accounts.forCompany` — the Bliks account for a HubSpot company, made
 * the first time anyone names the company (SAL-04). Goes through the same
 * service as the superadmin, so the company gets its back-reference and its
 * master data read.
 */ export const AppAccountForCompanyRequestSchema = z.object({
    hubspotCompanyId: z.string().regex(/^\d+$/, 'A HubSpot company id is numeric')
});
/**
 * A person `ctx.accounts.createWorkspace` puts into the new workspace. No
 * roles means a plain member; an external person (someone from the creating
 * partner) needs the organisation they come from and an elevated role.
 */ export const AppWorkspaceInviteeSchema = z.object({
    firstName: z.string().trim().min(1),
    lastName: z.string().trim().min(1),
    email: z.email(),
    userTypes: z.array(UserTypeSchema),
    classification: UserClassificationSchema,
    externalOrganization: z.string().trim().min(1).optional()
}).superRefine((data, ctx)=>refineExternalMember(ctx, data));
/**
 * `ctx.accounts.createWorkspace` — a workspace the calling tenant sets up
 * for someone else and then manages (plan 050). Only kinds the calling tenant
 * may create (its `provisioning`); every workspace but a demo belongs to an
 * account. Calling again with the same slug continues an earlier call that
 * stopped half-way, so a retry never makes a second workspace.
 */ export const AppWorkspaceCreateRequestSchema = z.object({
    accountId: ObjectIdSchema.optional(),
    name: z.string().trim().min(1),
    slug: TenantSlugSchema,
    tenantType: TenantTypeSchema,
    /** When a demo or trial ends; null or absent for no end. */ expiresAt: z.iso.datetime().nullable().optional(),
    description: TenantDescriptionSchema.optional(),
    /** Apps to unlock, by registry id. */ apps: z.array(z.string().min(1)).max(20).default([]),
    invitees: z.array(AppWorkspaceInviteeSchema).max(50).default([]),
    /** False makes the people without mailing them, e.g. for a demo. */ sendInvitations: z.boolean().default(true)
}).refine((data)=>data.tenantType === 'demo' || !!data.accountId, {
    message: 'Only a demo workspace may belong to no account',
    path: [
        'accountId'
    ]
});
const ManagedTenantIdSchema = ObjectIdSchema;
export const AppWorkspaceIdRequestSchema = z.object({
    tenantId: ManagedTenantIdSchema
});
/** `ctx.accounts.updateWorkspace` — name and end; what else a tenant has stays the superadmin's. */ export const AppWorkspaceUpdateRequestSchema = z.object({
    tenantId: ManagedTenantIdSchema,
    name: z.string().trim().min(1).max(120).optional(),
    /** A new end, or null to end it never. */ expiresAt: z.iso.datetime().nullable().optional()
}).refine((data)=>data.name !== undefined || data.expiresAt !== undefined, {
    message: 'Nothing to change'
});
/** `ctx.accounts.addWorkspaceApps` — unlock more apps; locking one stays the superadmin's. */ export const AppWorkspaceAppsRequestSchema = z.object({
    tenantId: ManagedTenantIdSchema,
    apps: z.array(z.string().min(1)).min(1).max(20)
});
/** `ctx.accounts.inviteToWorkspace` — more people into a managed workspace. */ export const AppWorkspaceInviteRequestSchema = z.object({
    tenantId: ManagedTenantIdSchema,
    invitees: z.array(AppWorkspaceInviteeSchema).min(1).max(50),
    sendInvitations: z.boolean().default(true)
});
/** Images one `ctx.ai.generateStructured` call may carry. */ export const APP_AI_IMAGE_LIMIT = 8;
/**
 * An image an app function shows the model. Like `ctx.files.create` bytes, it
 * crosses the worker boundary by structured clone as a plain `Uint8Array`.
 */ export const AppAiImageSchema = z.object({
    mediaType: z.enum([
        'image/png',
        'image/jpeg'
    ]),
    data: z.instanceof(Uint8Array).refine((data)=>data.byteLength <= MODEL_IMAGE_MAX_BYTES, {
        message: `An image may be at most ${MODEL_IMAGE_MAX_BYTES} bytes`
    })
});
/**
 * The optional part of a `ctx.ai.generateStructured` call. The SDK checks it
 * before posting and the parent parses it again: a caller that bypasses the
 * SDK reaches the parent unchecked.
 */ export const AppAiCallOptionsSchema = z.object({
    images: z.array(AppAiImageSchema).max(APP_AI_IMAGE_LIMIT).optional(),
    /**
   * Deadline of this one call. The parent aborts the provider request when it
   * passes, and never lets a call outlive the run's own deadline.
   */ timeoutMs: z.number().int().positive().optional(),
    /**
   * The manifest's `ai.features` key this call serves (plan 046). An app that
   * declares features must name one, and the call runs only while the tenant
   * has AI and that feature switched on.
   */ feature: z.string().optional()
});
// ========================================
// Regions (`ctx.regions`, SAL-14) — platform reference data, read-only
// ========================================
export const AppRegionGetRequestSchema = z.object({
    id: z.string().min(1).max(64)
});
export const AppRegionGetManyRequestSchema = z.object({
    ids: z.array(z.string().min(1).max(64)).max(REGION_GET_MANY_MAX)
});
export const AppRegionSearchRequestSchema = RegionSearchSchema;
export const AppRegionListRequestSchema = RegionListSchema;

//# sourceMappingURL=app-gateway.schema.js.map